import { create } from 'zustand';

/**
 * Experiments that are defined in posthog should be added to EXPERIMENTS.
 * The reason for having this in a separate file is that it allows us to import the types in the e2e tests.
 *
 * The structure here is: `Record<experimentName, { key: <key>, contenderVariant: <value> }>`
 *
 * The default variant is `control`, inactive experiments will be `undefined`.
 */
const EXPERIMENTS = Object.freeze({}) satisfies Record<string, ExperimentConfig>;

const experimentEntries = Object.entries(EXPERIMENTS) as [PH_EXP_NAMES, ExperimentConfig][];

const posthogLoadedStore = create(() => ({ posthogLoaded: false }));

// Check for e2e test overrides
const TEST_OVERRIDES = ((globalThis as any)._e2ePosthogEnabledExperiments as undefined | PH_EXP_NAMES[]) ?? [];
const waitForPosthogFeatureFlags = TEST_OVERRIDES.length === 0;

/**
 * Maps the experiment store state using the provided variant selector function.
 * @param variantSelector - A function that takes the experiment name and contender variant and returns the variant to use.
 */
const mapExperimentStoreState = (
  variantSelector: (key: string, name: PH_EXP_NAMES, contenderVariant: string) => string | null
) => {
  return experimentEntries.reduce((acc, [experiment, { key, contenderVariant }]) => {
    const variant = variantSelector(key, experiment, contenderVariant);
    return {
      ...acc,
      [experiment]: variant === contenderVariant,
      [`${experiment}Variant`]: variant,
    };
  }, {} as ExperimentStore);
};

/**
 * Wrap zustand store export so we can trigger posthog init on first use.
 */
const expStore = create<ExperimentStore>()(() => {
  // Apply overrides for e2e tests
  return mapExperimentStoreState((_key, name, contender) => (TEST_OVERRIDES.includes(name) ? contender : null));
});

export {
  experimentEntries,
  expStore,
  mapExperimentStoreState,
  type PH_EXP_NAMES,
  posthogLoadedStore,
  waitForPosthogFeatureFlags,
};

type ExperimentConfig = { key: string; contenderVariant: string };
type PH_EXP_NAMES = keyof typeof EXPERIMENTS;
type ExperimentStore = Record<PH_EXP_NAMES, boolean> & Record<`${PH_EXP_NAMES}Variant`, null | string>;
